import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { CARD_WIDTH } from '../design/tokens';
import type { TagMark } from '../folders/TagMark';
import { dayHead, monthHead } from '../design/dates';
import type { CorpusItem } from './corpus';
import type { buildGathering } from './gathering';
import { ThoughtCard } from './ThoughtCard';
import type { useThoughtWindow } from './useThoughtWindow';
import { estimateHeight, packColumns } from './wall';

/**
 * Thoughts as the Thoughts page draws them: cards packed into columns under
 * day heads, or month heads further back. Shared with the folder page, which
 * draws the same wall over a folder's members. A column is one card wide, so
 * a card is the width it is everywhere else it is drawn.
 */

/** Day heads this far back; month heads beyond, where a day holds too little
 *  to be worth a head of its own. */
const DAY_HEADS_WITHIN = 14 * 86_400_000;

export interface ThoughtDay {
  head: string;
  items: CorpusItem[];
  /** The head names one day, so a card under it need not. */
  saysDay: boolean;
}

/** Runs of the feed under one head — a day's, or further back a month's. */
export function groupByDay(items: readonly CorpusItem[], now: Date): ThoughtDay[] {
  const out: ThoughtDay[] = [];
  for (const item of items) {
    const saysDay = !!item.at && now.getTime() - item.at.getTime() <= DAY_HEADS_WITHIN;
    const head = !item.at ? 'Undated' : saysDay ? dayHead(item.at, now) : monthHead(item.at);
    const last = out[out.length - 1];
    if (last?.head === head) last.items.push(item);
    else out.push({ head, items: [item], saysDay });
  }
  return out;
}

/** Between the wall's columns, and between its cards. */
export const WALL_GAP = 22;

/** How many card-wide columns [width] holds: at least one, which then
 *  narrows to fit. */
export function columnsFor(width: number): number {
  return Math.max(1, Math.floor((width + WALL_GAP) / (CARD_WIDTH + WALL_GAP)));
}

/** How wide [columns] of cards stand, gaps and all. */
export function wallWidth(columns: number): number {
  return columns * CARD_WIDTH + (columns - 1) * WALL_GAP;
}

/** As many columns as [box]'s content holds, following its size — from
 *  whenever the element is there, which on a page that loads first is not
 *  its first render. */
export function useColumnCount(box: RefObject<HTMLElement | null>): number {
  const [count, setCount] = useState(() => (typeof window === 'undefined' ? 3 : columnsFor(window.innerWidth - 96)));
  const watched = useRef<{ el: HTMLElement; observer: ResizeObserver | null } | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (el === (watched.current?.el ?? null)) return;
    watched.current?.observer?.disconnect();
    watched.current = null;
    if (!el) return;
    const measure = () => {
      const style = getComputedStyle(el);
      const inner = el.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
      setCount(columnsFor(inner));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    watched.current = { el, observer };
  });
  useEffect(() => () => watched.current?.observer?.disconnect(), []);
  return count;
}

export function ThoughtDays({
  days,
  columns,
  opened,
  gathering,
  fillOf,
  markOf,
  focused = null,
  selected,
  onSelect,
  onOpen,
}: {
  days: ThoughtDay[];
  columns: number;
  opened: ReturnType<typeof useThoughtWindow>;
  gathering: ReturnType<typeof buildGathering>;
  fillOf: (path: string) => string;
  markOf?: (path: string) => TagMark | null;
  /** The card j / k has reached, by path. */
  focused?: string | null;
  /** Paths gathered into a selection. */
  selected?: ReadonlySet<string>;
  /** Shift-click gathers a card instead of opening it. Absent where nothing
   *  can be done with a selection. */
  onSelect?: (item: CorpusItem) => void;
  onOpen: (item: CorpusItem) => void;
}) {
  return (
    <>
      {days.map((day) => (
        <section key={day.head} className="thw-day" style={{ '--wallWidth': `${wallWidth(columns)}px` } as CSSProperties}>
          <h2 className="thw-head">{day.head}</h2>
          <div
            className="thw-wall"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, ${CARD_WIDTH}px))`, gap: WALL_GAP }}
          >
            {packColumns(day.items, columns, (item) => estimateHeight(opened.get(item.path))).map((column, c) => (
              <div key={c} className="thw-column">
                {column.map((item) => (
                  <ThoughtCard
                    key={item.path}
                    item={item}
                    thought={opened.get(item.path)}
                    gatheredIn={gathering.get(item.path) ?? []}
                    fill={fillOf(item.path)}
                    mark={markOf?.(item.path)}
                    dayShown={day.saysDay}
                    focused={item.path === focused}
                    selected={selected?.has(item.path)}
                    onOpen={(event) => {
                      if (onSelect && event.shiftKey) {
                        event.preventDefault();
                        onSelect(item);
                      } else onOpen(item);
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
